import { Fragment, RefObject, useMemo, type ReactNode } from 'react';
import { renderProvenanceItem } from './ProvenanceItemRenderer';
import { projectObservations } from './observation-labels';
import type { AgentHistoryItem, CollapsedSequenceItem } from './provenance-entry-item';
import type { MoveObservationMediaResult } from '@vivport/backend/types';

const COLLAPSE_THRESHOLD = 4;

function isToolItem(item: AgentHistoryItem): boolean {
  return item.kind === 'tool_call' || item.kind === 'tool_result';
}

function flushRun(run: AgentHistoryItem[], out: AgentHistoryItem[]): void {
  if (run.length === 0) return;
  let resultCount = 0;
  let hasError = false;
  for (const item of run) {
    if (item.kind === 'tool_result') {
      resultCount += 1;
      if (item.status === 'error') hasError = true;
    }
  }
  if (resultCount >= COLLAPSE_THRESHOLD && !hasError) {
    const collapsed: CollapsedSequenceItem = {
      id: `cs-${run[0].id}`,
      kind: 'collapsed_sequence',
      items: [...run],
    };
    out.push(collapsed);
    return;
  }
  for (const item of run) out.push(item);
}

function deriveCollapsedItems(items: AgentHistoryItem[]): AgentHistoryItem[] {
  const out: AgentHistoryItem[] = [];
  let run: AgentHistoryItem[] = [];
  for (const item of items) {
    if (isToolItem(item)) {
      run.push(item);
    } else {
      flushRun(run, out);
      run = [];
      out.push(item);
    }
  }
  flushRun(run, out);
  return out;
}

export interface ProvenanceFeedProps {
  items: AgentHistoryItem[];
  bottomRef: RefObject<HTMLDivElement>;
  onRequestCapture?: (args: { mode: 'recording' | 'photo' | 'video'; linkContentId?: number }) => void;
  onOpenContent?: (contentId: number) => void;
  onObservationMediaMoved?: (result: MoveObservationMediaResult) => void;
  audioUrlsByRecordingItemId?: Record<string, string>;
  density?: 'comfortable' | 'compact';
  ephemeral?: ReactNode;
}

export function ProvenanceFeed({
  items,
  bottomRef,
  onRequestCapture,
  onOpenContent,
  onObservationMediaMoved,
  audioUrlsByRecordingItemId,
  density = 'compact',
  ephemeral,
}: ProvenanceFeedProps) {
  const outerSpacing = density === 'comfortable' ? 'space-y-4' : 'space-y-3';
  const groupedItems = useMemo(() => deriveCollapsedItems(items), [items]);
  const observations = useMemo(() => projectObservations(items), [items]);
  return (
    <div
      data-testid="speak2me-history-feed"
      className={`flex-1 overflow-y-auto px-4 pb-3 pt-1.5 ${outerSpacing} scrollbar-thin`}
    >
      {groupedItems.map((item) => (
        <Fragment key={`${item.kind}:${item.id}`}>
          {renderProvenanceItem(item, {
            observations,
            onRequestCapture,
            onOpenContent,
            onObservationMediaMoved,
            audioUrlsByRecordingItemId,
            density,
          })}
        </Fragment>
      ))}
      {ephemeral}
      <div ref={bottomRef} />
    </div>
  );
}
